import { WebSocketSingleton } from "@utils/WebSocket";

import "./styles.css";
import { useMap } from "@vis.gl/react-google-maps";
import { memo, useEffect, useState } from "react";
import MapInstance from "./mapInstance";
import MissionMarkers from "./missionMarkers";
import type { Mission } from "./type";

const socket = WebSocketSingleton.getInstance();

type Props = {
  data?: Mission[];
  liveTracking?: boolean;
  newDelivery?: boolean;
  height?: string;
};

function CustomMap({ data, liveTracking = false }: Props) {
  const map = useMap();

  if (!data) throw new Error("Data is required");
  const [selectedMission, setSelectedMission] = useState<string | null>(() => {
    return data.length === 1 ? data[0].id.toString() : null;
  });

  const destinationsFitKey = JSON.stringify(
    [...data]
      .map((d) => [d.id, d.destination.lat, d.destination.lng] as const)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
  );

  const liveTrackingRoomKey = [...data]
    .map((m) => m.id)
    .sort()
    .join(",");

  // `destinationsFitKey` replaces `data` in deps: parent often passes a new array each render with identical points; refitting would loop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see comment above
  useEffect(() => {
    const bounds = new google.maps.LatLngBounds();
    if (map) {
      data.forEach((d) => {
        bounds.extend(d.destination);
      });
      map.fitBounds(bounds);
    }
  }, [map, destinationsFitKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `liveTrackingRoomKey` replaces `data` to avoid reconnect/join thrash on referential churn
  useEffect(() => {
    if (liveTracking) {
      socket.connect();
      data.forEach((mission) => {
        socket.joinRoom(+mission.id);
      });
    }
    return () => {
      if (liveTracking) {
        socket.close();
      }
    };
  }, [liveTracking, liveTrackingRoomKey]);

  return (
    <MapInstance>
      {data?.length
        ? data
            // .filter((d) => d.id.toString() === '638')
            .map((mission) => (
              <MissionMarkers
                key={mission.id}
                mission={mission}
                selected={selectedMission}
                setSelectedMission={setSelectedMission}
              />
            ))
        : null}
    </MapInstance>
  );
}

export default memo(CustomMap);
